import { getCsrfToken } from '../../utils/auth';

// Product structures (2026-10-28) — `/api/bom/structures/*` (bom_struct_api.py).
// Design: design/DESIGN_BOM_HIERARCHY.md. The hierarchy is kept in OUR database; parts are
// read live from Orcanos by the server. Kept apart from the hardware tree's client
// (bom/api/orcanosClient.js) so nothing here can touch the Orcanos structure (Note #61).

export class PsError extends Error {
  constructor(status, message) {
    super(message || `Request failed (HTTP ${status})`);
    this.httpCode = status;
  }
}

async function call(path, { method = 'GET', body } = {}) {
  const headers = { Accept: 'application/json' };
  if (method !== 'GET') headers['X-CSRF-Token'] = getCsrfToken();
  if (body !== undefined) headers['Content-Type'] = 'application/json';
  let resp;
  try {
    resp = await fetch(`/api/bom/structures${path}`, {
      method, headers, body: body === undefined ? undefined : JSON.stringify(body), credentials: 'same-origin',
    });
  } catch {
    throw new PsError(0, 'Could not reach the server. Check your connection and try again.');
  }
  let data = null;
  try { data = await resp.json(); } catch { /* not JSON */ }
  if (!resp.ok) {
    const d = data && data.detail;
    throw new PsError(resp.status, typeof d === 'string' ? d : `Request failed (HTTP ${resp.status})`);
  }
  return data;
}

const q = (o) => Object.entries(o).filter(([, v]) => v !== undefined && v !== null && v !== '')
  .map(([k, v]) => `${k}=${encodeURIComponent(v)}`).join('&');

export const psApi = {
  roots: () => call('/roots'),
  search: (text, docs = true) => call(`/search?${q({ q: text, docs: docs ? undefined : 'false' })}`),
  addRoot: (node) => call('/roots', { method: 'POST', body: { node } }),
  removeRoot: (node) => call(`/roots/${encodeURIComponent(node)}`, { method: 'DELETE' }),
  copyOrcanos: (root) => call('/copy-orcanos', { method: 'POST', body: { root } }),
  tree: (root, branch) => call(`/tree?${q({ root, branch })}`),
  addLink: (body) => call('/links', { method: 'POST', body }),
  patchLink: (id, body) => call(`/links/${id}`, { method: 'PATCH', body }),
  deleteLink: (id, root) => call(`/links/${id}?${q({ root })}`, { method: 'DELETE' }),
  moveLink: (id, root, newParent) => call(`/links/${id}/move`, { method: 'POST', body: { root, new_parent: newParent } }),
  useShared: (root, parent) => call(`/overrides?${q({ root, parent })}`, { method: 'DELETE' }),
  lock: (root, node, comment = '') => call('/locks', { method: 'POST', body: { root, node, comment } }),
  unlock: (root, node, comment = '') => call(`/locks?${q({ root, node, comment: comment || undefined })}`, { method: 'DELETE' }),
  setCost: (root, node, cost) => call('/cost', { method: 'POST', body: { root, node, cost } }),
  history: (root, beforeId) => call(`/history?${q({ root, before_id: beforeId })}`),
  whereUsed: (node) => call(`/where-used?${q({ node })}`),
  usage: () => call('/usage'),
  dashboard: () => call('/dashboard'),
  baselines: (root) => call(`/baselines?${q({ root })}`),
  baseline: (id) => call(`/baselines/${id}`),
  takeBaseline: (body) => call('/baselines', { method: 'POST', body }),
  deleteBaseline: (id) => call(`/baselines/${id}`, { method: 'DELETE' }),
  compare: (root, a, b) => call(`/compare?${q({ root, a, b })}`),
  exportRows: (o) => call(`/export-rows?${q(o)}`),
  branches: (root) => call(`/branches?${q({ root })}`),
  addBranch: (body) => call('/branches', { method: 'POST', body }),
  change: (id, body) => call(`/branches/${id}/changes`, { method: 'POST', body }),
  dropChange: (id, index) => call(`/branches/${id}/changes/${index}`, { method: 'DELETE' }),
  pull: (id, apply = false) => call(`/branches/${id}/pull${apply ? '?apply=true' : ''}`, { method: 'POST' }),
  merge: (id, scopes) => call(`/branches/${id}/merge`, { method: 'POST', body: { scopes } }),
  closeBranch: (id) => call(`/branches/${id}/close`, { method: 'POST' }),
  deleteBranch: (id) => call(`/branches/${id}`, { method: 'DELETE' }),
};

// BOM file import (2026-10-29) — `/api/bom/structures/imports/*` (bom_import_api.py).
async function upload(path, files, field) {
  const fd = new FormData();
  files.forEach((f) => fd.append(field, f, f.name));
  let resp;
  try {
    resp = await fetch(`/api/bom/structures/imports${path}`, {
      method: 'POST', body: fd, credentials: 'same-origin', headers: { 'X-CSRF-Token': getCsrfToken(), Accept: 'application/json' },
    });
  } catch {
    throw new PsError(0, 'Could not reach the server. Check your connection and try again.');
  }
  let data = null;
  try { data = await resp.json(); } catch { /* not JSON */ }
  if (!resp.ok) {
    const d = data && data.detail;
    throw new PsError(resp.status, typeof d === 'string' ? d : `Upload failed (HTTP ${resp.status})`);
  }
  return data;
}

export const importApi = {
  list: () => call('/imports'),
  get: (id) => call(`/imports/${id}`),
  upload: (file) => upload('', [file], 'file'),
  addImages: (id, files) => upload(`/${id}/images`, files, 'files'),
  clearImages: (id) => call(`/imports/${id}/images`, { method: 'DELETE' }),
  formFields: () => call('/imports/form-fields'),
  mapping: (id, body) => call(`/imports/${id}/mapping`, { method: 'PUT', body }),
  target: (id, body) => call(`/imports/${id}/target`, { method: 'PUT', body }),
  picks: (id, picks) => call(`/imports/${id}/picks`, { method: 'PUT', body: { picks } }),
  review: (id) => call(`/imports/${id}/review`),
  create: (id) => call(`/imports/${id}/create`, { method: 'POST' }),
  stop: (id) => call(`/imports/${id}/stop`, { method: 'POST' }),
  remove: (id) => call(`/imports/${id}`, { method: 'DELETE' }),
};

export const fmtMoney = (n) => {
  const [i, d] = Number(n || 0).toFixed(2).split('.');
  return i.replace(/\B(?=(\d{3})+(?!\d))/g, ',') + '.' + d;
};

export const fmtQty = (n) => {
  const f = Number(n);
  if (!Number.isFinite(f)) return '';
  return Number.isInteger(f) ? String(f) : String(Math.round(f * 1000) / 1000);
};

export const statusTone = (s) => {
  const v = String(s || '').toLowerCase();
  if (!v) return '';
  if (['released', 'approved', 'active', 'effective'].includes(v)) return 'ok';
  if (['obsolete', 'inactive', 'cancelled', 'canceled', 'missing'].includes(v)) return 'bad';
  return 'warn';
};
